import { FC, useEffect, useRef } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import clsx from 'clsx'
import { reaction } from 'mobx'
import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes'

import { prepareCountPassengers } from 'entities/favorite/lib/prepare-count-passengers'
import { getOrCreateAviaSearchId } from 'entities/ticket/lib/avia-search-id'
import { isDifficultAviaRoute } from 'entities/ticket/lib/is-difficult-avia-route'
import findTicketFormStore from 'entities/ticket/models/find-ticket/find-ticket-store'
import { IFindTicketForm } from 'entities/ticket/models/find-ticket/models'
import listTicketsStore from 'entities/ticket/models/list-tickets/list-tickets-store'
import { TYPE_TIMER } from 'entities/ticket/models/timer'
import timerStore from 'entities/ticket/models/timer/timer-store'
import { CardOfTicket } from 'entities/ticket/ui/card-of-ticket/ui'

import { SavedFormKeys, saveForm } from 'shared/hooks'
import { useInfiniteScroll } from 'shared/hooks/use-infinity-scroll.ts'
import RefetchTime from 'shared/images/clock_image.png'
import FlightBannerImage from 'shared/images/flight-banner.webp'
import ImageGlass from 'shared/images/glasss.png'
import { prepareShowListTicket } from 'shared/lib//ticketRender'
import { sendGtagEvent } from 'shared/lib/analytics'
import { AppAdviceText } from 'shared/ui/app-advice-text'
import { BannerPromo } from 'shared/ui/banner-promo'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { EmptyResults } from 'shared/ui/empty-result'
import { Loader } from 'shared/ui/loader'
import { PopupContent } from 'shared/ui/poup-content'
import { SimpleLoader } from 'shared/ui/simple-loader'

// import { BannerInfo } from './components/banner-info'
import { FilterBar } from './components/fitler-bar'
import { Header } from './components/header/ui'
import s from './styles.module.scss'

const EMPTY_PAGE = 0

interface Props {
  formSearchAvia: IFindTicketForm
}

export const ListResultsSearch: FC<Props> = observer(({ formSearchAvia }) => {
  const navigate = useNavigate()

  const { t } = useTranslation()

  const directions = findTicketFormStore.formFindTicket?.directions

  const isDifficultRoute = isDifficultAviaRoute(directions)

  const { closeDialog, openDialog } = useDialogBottomSheet()

  const listTicket = listTicketsStore.getListTickets()

  const filter = findTicketFormStore.formFindTicket?.filter

  const timerData = timerStore.getTimerData(TYPE_TIMER.RESULT_LIST)

  const targetRef = useRef<HTMLDivElement>(null!)

  useInfiniteScroll({
    targetRef,
    canLoad: listTicketsStore.page <= listTicketsStore.maxPage,
    onLoad: () => {
      void listTicketsStore.onGetTicketRequest({
        form: formSearchAvia,
        params: {
          isDifficultRoute,
        },
      })
    },
    onLoadComplete: () => {
      if (listTicketsStore.page === 1 && !isDifficultRoute) {
        saveForm(
          {
            ...formSearchAvia,
            filter: undefined,
          },
          SavedFormKeys.avia.key,
        )
      }
    },
  })

  reaction(
    () => timerData?.isRunning,
    (isRunning) => {
      if (
        isRunning === false &&
        window.location.pathname === RoutingPaths.avia.search.path()
      ) {
        showPopUpTimeOver()
      }
    },
  )

  const showPopUpTimeOver = () => {
    openDialog({
      isClickable: false,
      isDialog: false,
      child: (
        <PopupContent
          button={t('refresh')}
          image={RefetchTime}
          title={t('refresh_datas')}
          description={t('refresh_datas_des')}
          onClick={() => {
            closeDialog()
            forceSearch()
          }}
        />
      ),
    })
  }

  const forceSearch = () => {
    listTicketsStore.onNewListOfTicket({ isDifficultRoute })
  }

  const getListResultsByTimeline = (startDate: string, endDate: string) => {
    findTicketFormStore.resetFilter()
    findTicketFormStore.onSetRangeDate({ rangeData: { startDate, endDate } })

    getOrCreateAviaSearchId(findTicketFormStore.formFindTicket)
    forceSearch()
  }

  const onGoBack = () => {
    navigate(RoutingPaths.path())

    listTicketsStore.resetListOfTicket()
  }

  const onClickDetail = (index: number) => {
    listTicketsStore.setActiveTicketIndex(index)

    navigate(RoutingPaths.avia.detailTicket.path(String(index)))
  }

  useEffect(() => {
    if (listTicketsStore.activeTicketIndex === null) return

    const ticketCard = document.getElementById(
      `ticket-card-${listTicketsStore.activeTicketIndex}`,
    )

    if (ticketCard) {
      ticketCard.scrollIntoView({ block: 'center' })
    }
  }, [])

  const totalCountPassengers = findTicketFormStore.getCountOfPassengers()

  const handleClickTourBanner = () => {
    sendGtagEvent('VZR_SEARCH_BANNER_CLICK')
    navigate(RoutingPaths.insurance.path())
  }

  const isShowFilter =
    Boolean(filter) || listTicketsStore.listOfTickets?.length > EMPTY_PAGE

  return (
    <div className={s.wrapper}>
      <div className={clsx('safe-area-top-padding', s.header)}>
        <Header
          isDifficultRoute={isDifficultRoute}
          onGoBack={onGoBack}
          onSearchChangeCount={forceSearch}
          getListResultsByTimeline={getListResultsByTimeline}
        />

        {!listTicketsStore.loading && isShowFilter && (
          <>
            <FilterBar />
            {/* <BannerInfo
              isActive={isPromoCodeAvailable}
              directions={findTicketFormStore.formFindTicket?.directions}
            /> */}
          </>
        )}
      </div>

      {listTicketsStore?.loading &&
      listTicketsStore.listOfTickets?.length === EMPTY_PAGE ? (
        <div className={s.loader}>
          <Loader marginTop={200} />

          <AppAdviceText />
        </div>
      ) : null}

      <div className={clsx('container', s.container)}>
        {listTicket?.map((ticket, index) => {
          const preparedTicket = prepareShowListTicket(
            ticket,
            totalCountPassengers,
          )

          return (
            <div className={s.item}>
              <CardOfTicket
                isMbonus
                index={index}
                ticket={preparedTicket}
                directions={directions}
                searchParams={prepareCountPassengers(
                  findTicketFormStore.formFindTicket,
                )}
                key={preparedTicket?.favorite_search_key}
                isSavedTicket={index === listTicketsStore.activeTicketIndex}
                onClick={() => onClickDetail(index)}
              />

              {index === 0 && (
                <div className={s.tourBanner}>
                  <BannerPromo
                    image={FlightBannerImage}
                    title={t('banner_info.title')}
                    description={t('banner_info.description')}
                    onClick={handleClickTourBanner}
                  />
                </div>
              )}
            </div>
          )
        })}
      </div>

      {listTicket?.length === EMPTY_PAGE && !listTicketsStore.loading ? (
        <div className={clsx('container', s.emptyBlock)}>
          <EmptyResults
            image={
              <img
                src={ImageGlass}
                alt="test"
                className={s.image}
              />
            }
            title={t('search_ticket.empty_title')}
            subTitle={t('search_ticket.empty_page')}
          />
        </div>
      ) : null}

      {listTicketsStore?.loading && listTicketsStore.listOfTickets?.length ? (
        <div className={s.loader}>
          <SimpleLoader />
        </div>
      ) : null}

      <div ref={targetRef} />
    </div>
  )
})
